import { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { CheckCircle, ImagePlus, Mail, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function formatDateTime(value) {
  return value ? new Date(value).toLocaleString('pt-BR') : '-';
}

export default function PublicDisplayCustomizationPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [request, setRequest] = useState(null);
  const [success, setSuccess] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [formData, setFormData] = useState({
    visitor_name: '',
    display_message: ''
  });

  useEffect(() => {
    fetchRequest();
  }, [token]);

  const fetchRequest = async () => {
    try {
      const response = await axios.get(`${API}/public/display-customization/${token}`);
      setRequest(response.data);
      setFormData({
        visitor_name: response.data.visitor_name || '',
        display_message: response.data.display_message || ''
      });
      setImagePreview(response.data.welcome_image || null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Nao foi possivel carregar a personalizacao');
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    if (!file.type.startsWith('image/')) {
      toast.error('Selecione um arquivo de imagem valido');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('A imagem deve ter no maximo 5MB');
      return;
    }

    setImageFile(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const clearImage = () => {
    setImageFile(null);
    setImagePreview(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);

    try {
      let welcomeImageUrl = request?.welcome_image || null;

      if (imageFile) {
        const payload = new FormData();
        payload.append('file', imageFile);
        const uploadResponse = await axios.post(`${API}/public/upload-image`, payload, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        welcomeImageUrl = uploadResponse.data.url;
      } else if (!imagePreview) {
        welcomeImageUrl = null;
      }

      const response = await axios.post(`${API}/public/display-customization/${token}`, {
        visitor_name: formData.visitor_name || null,
        display_message: formData.display_message || null,
        welcome_image: welcomeImageUrl
      });

      setSuccess(response.data);
      toast.success(response.data?.message || 'Personalizacao salva');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Nao foi possivel salvar a personalizacao');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!request) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-6">
        <div className="max-w-xl w-full bg-white rounded-3xl border border-border/40 p-10 shadow-xl text-center">
          <h1 className="text-2xl font-manrope font-bold tracking-tight mb-3">Solicitacao indisponivel</h1>
          <p className="text-muted-foreground">O link pode estar invalido ou nao existir mais.</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-6">
        <div className="max-w-2xl w-full bg-white rounded-3xl border border-border/40 p-10 shadow-xl text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-3xl font-manrope font-bold tracking-tight mb-3">Display atualizado</h1>
          <p className="text-muted-foreground mb-6">{success.message}</p>
          <div className="text-left bg-gray-50 rounded-2xl p-5 space-y-2">
            <p><strong>Reuniao:</strong> {request.meeting_name}</p>
            <p><strong>Sala:</strong> {request.room_name}</p>
            <p><strong>Nome exibido:</strong> {success.visitor_name || 'Nao informado'}</p>
            <p><strong>Mensagem:</strong> {success.display_message || 'Nao informada'}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border/40 bg-white/60 backdrop-blur-sm">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-6 flex items-center gap-3">
          <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center">
            <Mail className="w-6 h-6 text-primary-foreground" />
          </div>
          <div>
            <h1 className="text-2xl font-manrope font-bold tracking-tight">Personalizar display da sala</h1>
            <p className="text-sm text-muted-foreground">Escolha a imagem e a mensagem que serao exibidas no monitor</p>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 md:px-12 py-10 space-y-8">
        <div className="bg-white rounded-3xl border border-border/40 p-8 shadow-sm">
          <h2 className="text-3xl font-manrope font-bold tracking-tight mb-4">{request.meeting_name}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="rounded-2xl bg-gray-50 p-4">
              <p className="text-muted-foreground mb-1">Email de confirmacao</p>
              <p className="font-medium break-all">{request.recipient_email || '-'}</p>
            </div>
            <div className="rounded-2xl bg-gray-50 p-4">
              <p className="text-muted-foreground mb-1">Sala</p>
              <p className="font-medium">{request.room_name || '-'}</p>
            </div>
            <div className="rounded-2xl bg-gray-50 p-4">
              <p className="text-muted-foreground mb-1">Inicio</p>
              <p className="font-medium">{formatDateTime(request.start_time)}</p>
            </div>
            <div className="rounded-2xl bg-gray-50 p-4">
              <p className="text-muted-foreground mb-1">Fim</p>
              <p className="font-medium">{formatDateTime(request.end_time)}</p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-3xl border border-border/40 p-8 shadow-sm space-y-6">
          <div className="space-y-2">
            <Label htmlFor="visitor_name">Nome em destaque</Label>
            <Input
              id="visitor_name"
              value={formData.visitor_name}
              onChange={(event) => setFormData((prev) => ({ ...prev, visitor_name: event.target.value }))}
              placeholder="Ex: Cliente XPTO"
              className="h-12"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="display_message">Mensagem no display</Label>
            <Textarea
              id="display_message"
              value={formData.display_message}
              onChange={(event) => setFormData((prev) => ({ ...prev, display_message: event.target.value }))}
              placeholder="Ex: Bem-vindo ao nosso escritorio. Sua reuniao com a equipe comercial comeca em instantes."
              rows={5}
            />
          </div>

          <div className="space-y-2">
            <Label>Imagem para o display</Label>
            {imagePreview ? (
              <div className="relative">
                <div className="relative w-full aspect-[9/16] max-h-72 bg-gray-100 rounded-lg overflow-hidden">
                  <img src={imagePreview} alt="Preview do display" className="w-full h-full object-cover" />
                </div>
                <button
                  type="button"
                  onClick={clearImage}
                  className="absolute -top-2 -right-2 bg-destructive text-white rounded-full p-1 hover:bg-destructive/90 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <label className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-border/50 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  <ImagePlus className="w-8 h-8 mb-2 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">Clique para enviar uma imagem</p>
                  <p className="text-xs text-muted-foreground mt-1">Proporcao ideal: 9x16 (vertical)</p>
                </div>
                <input type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
              </label>
            )}
          </div>

          <div className="flex justify-end">
            <Button type="submit" disabled={saving} className="rounded-full h-12 px-8">
              {saving ? 'Salvando...' : 'Salvar personalizacao'}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
}
